import * as React from "react";
import { useState } from "react";
import {
	Box,
	Button,
	Card,
	CardContent,
	CircularProgress,
	Divider,
	Drawer,
	IconButton,
	Stack,
	Switch,
	Tooltip,
	Typography,
} from "@mui/material";
import { GearIcon } from "@phosphor-icons/react/dist/ssr/Gear";
import { XIcon } from "@phosphor-icons/react/dist/ssr/X";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import { CampaignTool, Tool, useCampaignTools, useTools, useUpdateCampaignTool } from "@/hooks/api/use-tools";
import { CustomSkeleton } from "@/components/customSkeleton/custom-skeleton";
import { DynamicField } from "@/components/dynamicField/dynamic-field";
import { DynamicFieldType } from "@/components/dynamicField/types";

export function AItools(): React.JSX.Element {
	const { campaignId } = useParams();

	const { data: tools, isLoading: isToolsLoading } = useTools();
	const { data: campaignTools, isLoading: isCampaignToolsLoading, isFetching } = useCampaignTools(campaignId!);
	const updateCampaignToolMutation = useUpdateCampaignTool();

	const [selectedTool, setSelectedTool] = useState<Tool | null>(null);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [isShowDefault, setIsShowDefault] = useState(false);
	const [updatedSwitchName, setUpdatedSwitchName] = useState("");

	const methods = useForm<any>({
		defaultValues: {},
	});

	const {
		handleSubmit,
		reset,
		control,
		setValue,
		getValues,
		formState: { isSubmitting },
	} = methods;

	const handleGearClick = (tool: Tool) => {
		const selectedCampaignTool = tool ? campaignTools.find(({ name }) => tool.name === name) : null;

		reset({
			name: tool.name,
			description: selectedCampaignTool?.description || "",
			allowInterruption: !!selectedCampaignTool?.allowInterruption,
			settings: selectedCampaignTool.settings || {},
		});

		setSelectedTool(tool);
		setDrawerOpen(true);
	};

	const handleDrawerClose = () => {
		setDrawerOpen(false);
		setSelectedTool(null);
		setIsShowDefault(false);
		reset({});
	};

	const handleSave = async (values: any) => {
		if (selectedTool) {
			await updateCampaignToolMutation.mutateAsync({
				campaignId: campaignId!,
				name: selectedTool.name,
				data: {
					name: selectedTool.name,
					description: values.description,
					allowInterruption: values.allowInterruption,
					enabled: true,
					settings: values.settings,
				},
			});
		}
		handleDrawerClose();
	};

	const handleToggleEnabledStatus = async (tool: Tool, campaignExistedTool: CampaignTool) => {
		setUpdatedSwitchName(tool.name);

		try {
			if (campaignExistedTool) {
				await updateCampaignToolMutation.mutateAsync({
					campaignId: campaignId!,
					name: tool.name,
					data: {
						name: tool.name,
						description: campaignExistedTool.description,
						allowInterruption: campaignExistedTool.allowInterruption,
						enabled: !campaignExistedTool.enabled,
						settings: campaignExistedTool.settings,
					},
				});

				return;
			}

			if (tool?.fields?.length) {
				setSelectedTool(tool);
				reset({
					name: tool.name,
					description: "",
					allowInterruption: true,
					settings: {},
				});
				setDrawerOpen(true);
				return;
			}

			if (tool?.fields?.length === 0) {
				await updateCampaignToolMutation.mutateAsync({
					campaignId: campaignId!,
					name: tool.name,
					data: { name: tool.name, description: "", allowInterruption: true, enabled: true, settings: {} },
				});

				return;
			}
		} catch (error) {
			console.log(error);
		} finally {
			setUpdatedSwitchName("");
		}
	};

	const isLoading = isToolsLoading || isCampaignToolsLoading;
	const isSwitchDisabled = updateCampaignToolMutation.isPending || isLoading || isFetching;

	return (
		<Box sx={{ p: 3 }}>
			<Stack spacing={1.5}>
				<Box>
					<Typography variant="h6" fontWeight={500}>
						Tools
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.875rem" }}>
						Configure the tools.
					</Typography>
				</Box>

				<Card
					sx={{
						border: "1px solid",
						boxShadow: "none !important",
						borderColor: tools?.length > 0 || isLoading ? "divider" : "transparent",
					}}
				>
					<CardContent sx={{ p: 0, pb: "0 !important" }}>
						<Stack divider={<Divider />}>
							{isLoading ? (
								Array.from({ length: 3 }).map((_, index) => (
									<Box
										key={index}
										sx={{
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											p: 1.5,
											px: 3,
										}}
									>
										<Stack spacing={0.25} sx={{ flex: 1, pt: 0.5 }}>
											<CustomSkeleton variant="rounded" width={140} height={16} />

											<CustomSkeleton variant="rounded" width={240} height={16} sx={{ mt: 1 }} />
										</Stack>

										<Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
											<CustomSkeleton variant="rounded" width={40} height={20} sx={{ mt: 0 }} />
										</Stack>
									</Box>
								))
							) : tools?.length > 0 ? (
								tools?.map((tool) => {
									const campaignExistedTool = campaignTools.find(({ name }) => tool.name === name);

									return (
										<Box
											key={tool.id}
											sx={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
												p: 1.5,
												px: 3,
												minHeight: 70,
											}}
										>
											<Stack spacing={0.25} sx={{ flex: 1 }}>
												<Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.875rem" }}>
													{tool.title}
												</Typography>
												<Typography
													variant="body2"
													color="text.secondary"
													sx={{ fontSize: "0.8125rem", whiteSpace: "pre-wrap" }}
												>
													{tool.description || " "}
												</Typography>
											</Stack>

											<Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
												{updateCampaignToolMutation.isPending && updatedSwitchName === tool?.name ? (
													<Box
														sx={{
															width: 40,
															height: 40,
															display: "flex",
															justifyContent: "center",
															alignItems: "center",
														}}
													>
														<CircularProgress size={18} sx={{ color: "text.secondary" }} />
													</Box>
												) : campaignExistedTool?.enabled ? (
													<Tooltip title="Customize the tool" arrow>
														<IconButton
															size="medium"
															onClick={() => handleGearClick(tool)}
															sx={{ color: "text.secondary", p: 0.75 }}
														>
															<GearIcon fontSize="var(--icon-fontSize-md)" />
														</IconButton>
													</Tooltip>
												) : (
													<Box sx={{ width: 40, height: 40 }} />
												)}
												<Switch
													checked={!!campaignExistedTool?.enabled}
													size="medium"
													onChange={(event) => {
														if (isSwitchDisabled) {
															event.preventDefault();
															return;
														}
														handleToggleEnabledStatus(tool, campaignExistedTool);
													}}
													sx={{ pointerEvents: isSwitchDisabled ? "none" : "auto" }}
													inputProps={{ readOnly: isSwitchDisabled }}
													tabIndex={isSwitchDisabled ? -1 : 0}
													onKeyDown={(event) => {
														if (isSwitchDisabled && (event.key === " " || event.key === "Enter")) {
															event.preventDefault();
															event.stopPropagation();
														}
													}}
												/>
											</Stack>
										</Box>
									);
								})
							) : (
								<Typography
									variant="h6"
									fontSize={16}
									color="text.disabled"
									sx={{ display: "flex", justifyContent: "center", py: 2 }}
								>
									No tools available
								</Typography>
							)}
						</Stack>
					</CardContent>
				</Card>
			</Stack>

			<Drawer
				anchor="right"
				open={drawerOpen}
				onClose={handleDrawerClose}
				slotProps={{
					backdrop: { invisible: true },
					paper: {
						sx: {
							width: "600px",
							maxWidth: "100dvw",
							flex: 1,
							backgroundImage: "none",
							backgroundColor: "background.paper",
							display: "flex",
							flexDirection: "column",
							overflow: "hidden",
						},
					},
				}}
			>
				<Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
					{/* Header */}
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							py: 2,
							px: 2.5,
							borderBottom: "1px solid",
							borderColor: "divider",
						}}
					>
						<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
							<GearIcon fontSize="var(--icon-fontSize-md)" />
							<Typography variant="h6">Edit System Tool</Typography>
						</Stack>
						<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
							<IconButton size="small" onClick={handleDrawerClose}>
								<XIcon fontSize="var(--icon-fontSize-md)" />
							</IconButton>
						</Stack>
					</Box>

					{/* Content */}
					<Box sx={{ flex: 1, overflowY: "auto", p: 2.5, overflowX: "hidden" }}>
						{selectedTool && (
							<FormProvider {...methods}>
								<Stack spacing={2}>
									<Box>
										<Typography variant="subtitle1" fontWeight={600} gutterBottom>
											Configuration
										</Typography>
										<Typography variant="body2" color="text.secondary" gutterBottom>
											Describe to the LLM how and when to use the tool.
										</Typography>
									</Box>

									<DynamicField
										control={control}
										disabled={true}
										name="name"
										type={DynamicFieldType.TEXT}
										label="Name"
										dynamicField={{
											name: "name",
											label: "Name",
											type: DynamicFieldType.TEXT,
										}}
									/>

									<DynamicField
										control={control}
										name="description"
										type={DynamicFieldType.TEXT_AREA}
										label="Tool execution instruction (optional)"
										dynamicField={{
											name: "description",
											placeholder: "Enter Tool execution instruction",
											label: "Tool execution instruction",
											multiline: true,
											type: DynamicFieldType.TEXT_AREA,
										}}
										labelButton={
											<Button
												component="button"
												variant="text"
												size="small"
												onClick={(e) => {
													e.preventDefault();
													const values = getValues();
													if (isShowDefault) {
														setValue("description", values?.old_description);
														setIsShowDefault(false);
													} else {
														setValue("old_description", values?.description || "");
														setValue("description", selectedTool.llm.description);
														setIsShowDefault(true);
													}
												}}
												sx={{ cursor: "pointer", textTransform: "none", p: 0, px: 1 }}
											>
												{isShowDefault ? "Restore Original" : "Show Default"}
											</Button>
										}
									/>

									<DynamicField
										control={control}
										name="allowInterruption"
										type={DynamicFieldType.CHECKBOX}
										label="Allow interruptions"
										dynamicField={{
											name: "allowInterruption",
											label: "Allow interruptions",
											type: DynamicFieldType.CHECKBOX,
											description: "Select this box to allow interruptions while the tool is running.",
										}}
									/>

									{selectedTool?.fields?.map((field: any) => (
										<DynamicField
											control={control}
											type={field.type}
											name={`settings.${field.name}`}
											label={field.label}
											dynamicField={field}
											formName="settings"
											key={field.name}
										/>
									))}
								</Stack>
							</FormProvider>
						)}
					</Box>

					{/* Footer */}
					<Box
						sx={{
							display: "flex",
							justifyContent: "flex-end",
							alignItems: "center",
							py: 2,
							px: 2.5,
							borderTop: "1px solid",
							borderColor: "divider",
						}}
					>
						<Stack direction="row" spacing={1}>
							<Button variant="outlined" onClick={handleDrawerClose}>
								Cancel
							</Button>
							<Button
								variant="contained"
								onClick={handleSubmit(handleSave)}
								disabled={updateCampaignToolMutation.isPending || isSubmitting}
							>
								Save
							</Button>
						</Stack>
					</Box>
				</Box>
			</Drawer>
		</Box>
	);
}
